import { formatNumberValue } from '../../common/numberFormat';
import {
  DASHBOARD_DATA_STATE,
  DASHBOARD_VALUE_TYPE
} from './dashboardContract';

const DEFAULT_WIDGET_COLOURS = Object.freeze([
  '#11A5A4',
  '#3E7CB1',
  '#E8A93A',
  '#7B5EA7',
  '#D94B4B',
  '#4D9078'
]);

const finiteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const sortCalculatedDashboardWidgets = (widgets = []) =>
  [...widgets].sort(
    (left, right) =>
      left.displayOrder - right.displayOrder ||
      left.name.localeCompare(right.name)
  );

export const sortCalculatedWidgetMetrics = (metrics = []) =>
  [...metrics].sort((left, right) => left.seriesOrder - right.seriesOrder);

export const dashboardWidgetMetricHasData = (metric) =>
  metric?.dataState === DASHBOARD_DATA_STATE.AVAILABLE &&
  finiteNumber(metric.value) !== null;

export const formatDashboardWidgetValue = (metric, value) => {
  const numericValue = finiteNumber(value);
  if (numericValue === null) return 'No data';
  if (metric?.fieldSource && metric.fieldConfig) return formatNumberValue(numericValue, metric.fieldConfig);

  const decimalPlaces =
    metric?.valueType === DASHBOARD_VALUE_TYPE.INTEGER
      ? 0
      : Math.min(6, Math.max(0, Number(metric?.decimalPlaces) || 0));
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  }).format(numericValue);
  const unit =
    metric?.unitLabel ||
    (metric?.valueType === DASHBOARD_VALUE_TYPE.PERCENTAGE ? '%' : '');

  if (!unit) return formatted;
  return unit === '%' ? `${formatted}%` : `${formatted} ${unit}`;
};

export const formatDashboardWidgetMetricValue = (metric) =>
  dashboardWidgetMetricHasData(metric)
    ? formatDashboardWidgetValue(metric, metric.value)
    : 'No data';

export const formatDashboardProgressPercentage = (percentage) => {
  const numericValue = finiteNumber(percentage);
  if (numericValue === null) return 'No data';
  return `${new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1
  }).format(numericValue)}%`;
};

export const buildDashboardDistributionData = (widget) =>
  sortCalculatedWidgetMetrics(widget?.metrics)
    .map((metric, index) => ({
      colour:
        metric.colour ||
        DEFAULT_WIDGET_COLOURS[index % DEFAULT_WIDGET_COLOURS.length],
      formattedValue: formatDashboardWidgetMetricValue(metric),
      label: metric.label,
      metric,
      metricDefinitionId: metric.metricDefinitionId,
      value: dashboardWidgetMetricHasData(metric)
        ? finiteNumber(metric.value)
        : null
    }))
    .filter((item) => item.value !== null && item.value > 0);

export const dashboardDistributionHasMissingData = (widget) =>
  widget?.dataState === DASHBOARD_DATA_STATE.PARTIAL_DATA;

export const getDashboardProgressColour = (status) => {
  if (status === 'red') return 'error.main';
  if (status === 'yellow') return 'warning.main';
  if (status === 'green') return 'success.main';
  return 'text.disabled';
};
